// Logger estruturado com PII redaction. Espelha o mobile
// (`grana-money-app/src/shared/lib/logger.ts`):
//   - 5 namespaces canonicos: api, auth, nav, store, app.
//   - Niveis hierarquicos: debug < info < warn < error < silent.
//   - Em DEV: nivel `debug` (ve tudo). Em PROD: `error`. Em testes: `silent`.
//   - `addHttpBreadcrumb()` integra com Sentry (no-op se DSN ausente).
//
// PII redaction:
//   - Aplicada em qualquer argumento `object` antes de `console.*`.
//   - Chaves matching PII_KEYS sao trocadas por `[REDACTED]`.
//   - Recursivo (objetos aninhados, arrays) — mas aceitamos custo O(n)
//     porque logger nao e hot-path em prod (silenciado) e em dev a
//     legibilidade vence performance.
//   - email NAO redactado por default — necessario para diagnosticar
//     issues por usuario. Caso o produto requeira, adicionar `email`
//     ao regex.
//
// Sentry breadcrumb:
//   - `addHttpBreadcrumb` chama `addBreadcrumb` do sentry-setup, que
//     internamente faz no-op se SENTRY_DSN ausente. Mantemos esta funcao
//     no logger (em vez de importar sentry direto em `client.ts`) porque
//     mobile faz o mesmo: client e logger sao a unica camada que conhece
//     a stack de observability.
//
// Phase 04 substitui o stub Phase 03 — assinaturas mantidas (compativel).

import { ENV } from '@/shared/config/env';
import { addBreadcrumb } from '@/shared/observability/sentry-setup';

type Level = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const RANK: Record<Level, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 99,
};

const currentLevel: Level = ENV.IS_TEST ? 'silent' : ENV.IS_DEV ? 'debug' : 'error';

function shouldLog(level: Exclude<Level, 'silent'>): boolean {
  return RANK[level] >= RANK[currentLevel];
}

// Chaves comumente associadas a PII. Match case-insensitive como substring
// (`access_token` -> match em `data.access_token`, `user_password` -> match
// em `password`). Mantida em sync com mobile.
const PII_KEYS = /password|secret|token|cpf|cnpj|access_token|refresh_token|authorization/i;

// Limite anti-loop: redactor recursivo segura ciclos de referencia (objetos
// que apontam pra si mesmos). WeakSet preserva GC do objeto original.
function redact<T>(value: T, seen: WeakSet<object> = new WeakSet()): T {
  if (value === null || value === undefined) return value;
  const t = typeof value;
  if (t !== 'object') return value;
  // Narrow para `object` apos o guard de typeof — evita asserts redundantes
  // (eslint @typescript-eslint/no-unnecessary-type-assertion reclama).
  const obj: object = value;
  if (seen.has(obj)) return '[Circular]' as unknown as T;
  seen.add(obj);

  if (Array.isArray(value)) {
    return value.map((item: unknown) => redact(item, seen)) as unknown as T;
  }
  // Error preserva message (frequentemente util em logs) mas remove props
  // adicionais que possam carregar PII (ex: err.config.headers em axios).
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: ENV.IS_DEV ? value.stack : undefined,
    } as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (PII_KEYS.test(k)) {
      out[k] = '[REDACTED]';
    } else if (v !== null && typeof v === 'object') {
      out[k] = redact(v, seen);
    } else {
      out[k] = v;
    }
  }
  return out as T;
}

function buildLogger(prefix: string) {
  const tag = `[${prefix}]`;
  return {
    debug: (...args: unknown[]) => {
      if (shouldLog('debug')) console.debug(tag, ...args.map((a) => redact(a)));
    },
    info: (...args: unknown[]) => {
      if (shouldLog('info')) console.info(tag, ...args.map((a) => redact(a)));
    },
    warn: (...args: unknown[]) => {
      if (shouldLog('warn')) console.warn(tag, ...args.map((a) => redact(a)));
    },
    error: (...args: unknown[]) => {
      if (shouldLog('error')) console.error(tag, ...args.map((a) => redact(a)));
    },
  };
}

export const logger = {
  api: buildLogger('API'),
  auth: buildLogger('AUTH'),
  nav: buildLogger('NAV'),
  store: buildLogger('STORE'),
  app: buildLogger('APP'),
};

export interface HttpBreadcrumb {
  method: string;
  url: string;
  correlation_id: string;
  retry: boolean;
}

/**
 * Adiciona breadcrumb HTTP ao Sentry. No-op se `VITE_SENTRY_DSN` ausente
 * (sentry-setup.ts handler interno). Chamado pelo client interceptor a
 * cada tentativa HTTP — fica visivel no event timeline quando um erro
 * for capturado adiante.
 */
export function addHttpBreadcrumb(data: HttpBreadcrumb): void {
  addBreadcrumb({
    category: 'http',
    message: `${data.method} ${data.url}`,
    data: {
      correlation_id: data.correlation_id,
      retry: data.retry,
    },
  });
}

// Test-only: verificar shape do redactor com inputs controlados.
export const __testOnly__ = { redact, PII_KEYS };
